/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { identifyUser, resetSession } from '../../common/services/analytics/analytics';
import storage from 'redux-persist/lib/storage';
import {
  IInviteShareData,
  IUserData,
  IRedirectURI,
} from '../../common/services/user/user-data.interface';
import { IUserReferrersReferrals } from '../../common/services/user/user-referrerReferrals.interface';
import ENVIRONMENTS, {
  getKeycloakLogoutUrl,
  isConfiguredKeycloakUrl,
  QC_SSO_CLEARED_KEY,
} from '../../environments';
import { stashOnboardingLoginAlert } from '../../common/lib/onboarding-login-alert';
import { ONBOARDING_REFERRER_CLEARED_KEY } from '../../app/onboarding/container/onboarding-steps';

export interface CommonState {
  showLoader: boolean;
  auth?: {
    token?: string;
    idToken?: string;
  };
  redirectURI?: {
    url?: string;
    refCode?: string;
  };
  user?: IUserData;
  referrerReferrals?: IUserReferrersReferrals;
  inviteShare?: IInviteShareData;
}

const initialState: CommonState = {
  showLoader: false,
  auth: undefined,
};

const commonSlice = createSlice({
  initialState,
  name: 'common',
  reducers: {
    setAuth: (state, action: PayloadAction<{ token: string; idToken?: string }>) => {
      state.auth = action.payload;
    },
    renewAuthTokens: (
      state,
      action: PayloadAction<{ token: string; idToken?: string }>,
    ) => {
      if (!state.auth) return;
      state.auth.token = action.payload.token;
      if (action.payload.idToken) state.auth.idToken = action.payload.idToken;
    },
    logout: state => {
      const idTokenHint = state.auth?.idToken;
      stashOnboardingLoginAlert({
        source: 'logout reducer (session cleared)',
        reason: 'User session ended while on onboarding step',
      });
      storage.removeItem('persist:root');
      localStorage.removeItem('referringChannelId');
      localStorage.removeItem('channelId');
      try {
        sessionStorage.removeItem('refferingChannel');
        sessionStorage.removeItem(ONBOARDING_REFERRER_CLEARED_KEY);
      } catch {
        /* ignore */
      }
      state.auth = undefined;
      state.user = undefined;
      state.redirectURI = undefined;
      try {
        sessionStorage.setItem(QC_SSO_CLEARED_KEY, '1');
      } catch {
        /* ignore */
      }
      const afterLogout = `${window.location.origin}/`;
      const usesKeycloak = isConfiguredKeycloakUrl(ENVIRONMENTS.KEYCLOAK_ACCOUNT);
      const nextHref = usesKeycloak
        ? getKeycloakLogoutUrl(afterLogout, idTokenHint)
        : afterLogout;
      window.location.href = nextHref;
      try {
        resetSession();
      } catch (e) {
        console.error(e);
      }
    },
    dropRejectedSession: state => {
      state.auth = undefined;
      state.user = undefined;
    },
    clearLocalAuth: state => {
      storage.removeItem('persist:root');
      localStorage.removeItem('referringChannelId');
      localStorage.removeItem('channelId');
      try {
        sessionStorage.removeItem('refferingChannel');
        sessionStorage.removeItem(ONBOARDING_REFERRER_CLEARED_KEY);
      } catch {
        /* ignore */
      }
      state.auth = undefined;
      state.user = undefined;
      state.redirectURI = undefined;
    },
    setShowLoader: (state, action: PayloadAction<boolean>) => {
      state.showLoader = action.payload;
    },
    setRedirectURI: (state, action: PayloadAction<IRedirectURI>) => {
      state.redirectURI = action.payload;
    },
    setUserData: (state, action: PayloadAction<IUserData | undefined>) => {
      try {
        if (action.payload?._id) {
          identifyUser(action.payload._id, { email: action.payload._id });
        }
      } catch (e) {
        console.error(e);
      }
      state.user = action.payload;
    },
    setReferrerReferrals: (
      state,
      action: PayloadAction<IUserReferrersReferrals>,
    ) => {
      state.referrerReferrals = action.payload;
    },
    setInviteShareMessage: (state, action: PayloadAction<IInviteShareData>) => {
      state.inviteShare = action.payload;
    },
  },
});

const commonReducer = commonSlice.reducer;

export const {
  setAuth,
  renewAuthTokens,
  setShowLoader,
  setUserData,
  setRedirectURI,
  setReferrerReferrals,
  logout,
  dropRejectedSession,
  clearLocalAuth,
  setInviteShareMessage,
} = commonSlice.actions;

export default commonReducer;
